import { apiClient, getUserId } from "./authService";

export interface BannerPayload {
  user_id?: string;
  title?: string;
  description?: string;
  priority?: number;
  is_active?: boolean;
  image_url?: string;
}

type BannerId = string | number;

const ensureUserId = (provided?: string | null) => {
  const resolved = provided || getUserId();
  if (!resolved) {
    throw new Error("User ID is required for banner operations.");
  }
  return resolved;
};

const buildFormData = (
  payload: BannerPayload,
  file?: File | Blob | null
): FormData => {
  const formData = new FormData();

  const entries: [string, any][] = [
    ["user_id", payload.user_id],
    ["title", payload.title],
    ["description", payload.description],
    ["priority", payload.priority !== undefined ? String(payload.priority) : undefined],
    ["is_active", payload.is_active],
    ["image_url", payload.image_url],
  ];

  entries.forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      if (value instanceof Blob) {
        formData.append(key, value);
      } else if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
      } else {
        formData.append(key, String(value));
      }
    }
  });

  if (file) {
    formData.append("image", file);
  }

  return formData;
};

export const fetchBanners = async (userId?: string) => {
  try {
    const resolvedUserId = ensureUserId(userId);
    const response = await apiClient.get("/banners", {
      params: { user_id: resolvedUserId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchBannerById = async (id: BannerId) => {
  try {
    if (!id && id !== 0) {
      throw new Error("Banner ID is required.");
    }
    const response = await apiClient.get(`/banners/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createBanner = async (
  payload: BannerPayload,
  file?: File | Blob | null
) => {
  try {
    const resolvedUserId = ensureUserId(payload.user_id);
    const formData = buildFormData({ ...payload, user_id: resolvedUserId }, file);
    const response = await apiClient.post("/banners", formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateBanner = async (
  id: BannerId,
  payload: BannerPayload,
  file?: File | Blob | null
) => {
  try {
    if (!id && id !== 0) {
      throw new Error("Banner ID is required.");
    }
    const formData = buildFormData(payload, file);
    const response = await apiClient.put(`/banners/${id}`, formData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const toggleBanner = async (id: BannerId) => {
  try {
    if (!id && id !== 0) {
      throw new Error("Banner ID is required.");
    }
    const response = await apiClient.post(`/banners/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteBanner = async (id: BannerId) => {
  try {
    if (!id && id !== 0) {
      throw new Error("Banner ID is required.");
    }
    const response = await apiClient.delete(`/banners/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
